// Author: Akarsh Singh | 2026-09-14 | Identity-scoped comparison SWR and mutation epochs.
import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export interface ServiceComparisonQuery {
  userId: number;
  role: string;
  sessionId: string;
  serviceRfqId: number;
  quoteIds: number[];
}
interface Entry {
  subject: BehaviorSubject<any | null>;
  updatedAt: number;
  inFlight?: Promise<void>;
}

@Injectable({ providedIn: 'root' })
export class ServiceComparisonSWRService {
  private readonly cache = new Map<string, Entry>();
  private epoch = 0;
  private readonly ttlMs = 15_000;

  keyOf(q: ServiceComparisonQuery): string {
    // Preserve selected quote order; do not assume the procedure ignores it.
    return JSON.stringify([q.userId, q.role, q.sessionId, q.serviceRfqId, q.quoteIds]);
  }

  private entryFor(key: string): Entry {
    let entry = this.cache.get(key);
    if (!entry) {
      entry = { subject: new BehaviorSubject<any | null>(null), updatedAt: 0 };
      this.cache.set(key, entry);
    }
    return entry;
  }

  getList$(key: string) { return this.entryFor(key).subject.asObservable(); }

  getFreshOrClear(key: string): any | null {
    const entry = this.cache.get(key);
    if (!entry) return null;
    if (Date.now() - entry.updatedAt < this.ttlMs) return entry.subject.value;
    if (entry.subject.value !== null) entry.subject.next(null);
    return null;
  }

  refresh(key: string, fetcher: () => Promise<any>): Promise<void> {
    const entry = this.entryFor(key);
    if (entry.inFlight) return entry.inFlight;
    const startedUnder = this.epoch;
    const task = Promise.resolve().then(fetcher).then(response => {
      if (startedUnder !== this.epoch) return;
      entry.updatedAt = Date.now();
      entry.subject.next(response);
    }).finally(() => {
      if (entry.inFlight === task) entry.inFlight = undefined;
    });
    entry.inFlight = task;
    return task;
  }

  invalidateAll(): void {
    this.epoch++;
    this.cache.forEach(entry => {
      entry.updatedAt = 0;
      entry.inFlight = undefined;
      entry.subject.next(null);
    });
  }

  isMutation(url: string, method = 'POST'): boolean {
    if (['GET', 'HEAD', 'OPTIONS'].includes(method.toUpperCase())) return false;
    const path = url.split('?')[0];
    if (!path.includes('/services/rfq/')) return false;
    const action = path.split('/').pop() || '';
    return action === 'getServiceQuoteId' || !/^(fetch|get|generate|export|verify)/i.test(action);
  }
}
